import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { BudgetSeriesPoint } from '@/hooks/useSyncedData';

interface BudgetChartProps {
  data: BudgetSeriesPoint[];
}

const BudgetChart = ({ data: budgetData }: BudgetChartProps) => {
  if (budgetData.length === 0) {
    return (
      <div className="masira-card masira-slide-up" style={{ animationDelay: '500ms' }}>
        <h3 className="font-semibold text-foreground mb-4">Budget vs Actuals</h3>
        <p className="text-sm text-muted-foreground py-12 text-center">No projects synced yet.</p>
      </div>
    );
  }

  return (
    <div className="masira-card masira-slide-up" style={{ animationDelay: '500ms' }}>
      <h3 className="font-semibold text-foreground mb-4">Budget vs Actuals</h3>

      <div className="h-48 md:h-64">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={budgetData}>
            <defs>
              <linearGradient id="budgetGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(239, 84%, 67%)" stopOpacity={0.3} />
                <stop offset="95%" stopColor="hsl(239, 84%, 67%)" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="actualGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(160, 84%, 39%)" stopOpacity={0.3} />
                <stop offset="95%" stopColor="hsl(160, 84%, 39%)" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis
              dataKey="name"
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
              axisLine={{ stroke: 'hsl(var(--border))' }}
              interval="preserveStartEnd"
            />
            <YAxis
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }}
              axisLine={{ stroke: 'hsl(var(--border))' }}
              tickFormatter={(value) => `$${value / 1000}k`}
              width={45}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
              }}
              formatter={(value: number) => [`$${value.toLocaleString()}`, '']}
            />
            <Area
              type="monotone"
              dataKey="budget"
              stroke="hsl(239, 84%, 67%)"
              fill="url(#budgetGradient)"
              strokeWidth={2}
              name="Budget"
            />
            <Area
              type="monotone"
              dataKey="actual"
              stroke="hsl(160, 84%, 39%)"
              fill="url(#actualGradient)"
              strokeWidth={2}
              name="Actual"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="flex justify-center gap-4 md:gap-6 mt-4">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-primary" />
          <span className="text-xs md:text-sm text-muted-foreground">Budget</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-success" />
          <span className="text-xs md:text-sm text-muted-foreground">Actual</span>
        </div>
      </div>
    </div>
  );
};

export default BudgetChart;
